import { useEffect, useRef } from 'react';

import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { withWindow } from '@shared/utils/withWindow';

/** How soon a postponed deadline is checked again. */
export const LOAD_WATCHDOG_POSTPONE_MS = 1000;

type UseLoadWatchdogArgs = Readonly<{
  isLoading: boolean;
  /** Visible time, in ms, a load may take before `onTimeout`. No deadline when undefined or 0. Read when a load starts; later changes apply to the next load. */
  timeout?: number;
  onTimeout: () => void;
  /** Checked at the deadline. While it returns true the deadline moves `LOAD_WATCHDOG_POSTPONE_MS` later instead of firing. */
  shouldPostpone: () => boolean;
  /** Called at most once when a load ends without finishing: the component unmounts, or the page is hidden for good (a `pagehide` that is not entering the back/forward cache). */
  onAbandon?: (visibleMs: number) => void;
}>;

/**
 * Deadline for a component load, counted in time the page was visible. Hidden tabs throttle timers,
 * network and rendering, so a load can stall there with nobody waiting. The countdown pauses while
 * the page is hidden and resumes with the visible time left once it is shown again, so the deadline
 * is `timeout` ms of visible time in total, however often the tab is switched.
 */
export function useLoadWatchdog({ isLoading, timeout, onTimeout, shouldPostpone, onAbandon }: UseLoadWatchdogArgs): void {
  const stableOnTimeout = useEventCallback(onTimeout);
  const stableShouldPostpone = useEventCallback(shouldPostpone);
  const stableOnAbandon = useEventCallback((visibleMs: number) => onAbandon?.(visibleMs));
  // Read in the effect cleanup: still loading there means the component unmounted mid-load, rather
  // than the load having finished. A layout effect updates it before passive effect cleanups run.
  const isLoadingRef = useRef(isLoading);
  const timeoutRef = useRef(timeout);
  useIsomorphicLayoutEffect(() => {
    isLoadingRef.current = isLoading;
    timeoutRef.current = timeout;
  });

  useEffect(() => {
    if (!isLoading) return undefined;

    return withWindow(global => {
      const { document } = global;
      const isHidden = () => document.visibilityState === 'hidden';

      let visibleMsBeforeThisSpan = 0;
      let visibleSince = isHidden() ? undefined : Date.now();
      let deadlineVisibleMs = timeoutRef.current || undefined;
      let timeoutHandle: number | undefined;
      let hasTimedOut = false;
      let hasReportedAbandon = false;

      const visibleMs = () => visibleMsBeforeThisSpan + (visibleSince === undefined ? 0 : Date.now() - visibleSince);

      const arm = () => {
        if (hasTimedOut) return;
        global.clearTimeout(timeoutHandle);
        if (deadlineVisibleMs === undefined || visibleSince === undefined) return;
        timeoutHandle = global.setTimeout(onDeadline, Math.max(0, deadlineVisibleMs - visibleMs()));
      };

      const onDeadline = () => {
        if (stableShouldPostpone()) {
          deadlineVisibleMs = visibleMs() + LOAD_WATCHDOG_POSTPONE_MS;
          arm();
          return;
        }
        hasTimedOut = true;
        stableOnTimeout();
      };

      const onVisibilityChange = () => {
        if (isHidden()) {
          if (visibleSince === undefined) return;
          visibleMsBeforeThisSpan += Date.now() - visibleSince;
          visibleSince = undefined;
          global.clearTimeout(timeoutHandle);
        } else if (visibleSince === undefined) {
          visibleSince = Date.now();
          arm();
        }
      };

      const reportAbandon = () => {
        if (hasTimedOut || hasReportedAbandon) return;
        hasReportedAbandon = true;
        stableOnAbandon(visibleMs());
      };

      // A persisted pagehide enters the back/forward cache; the load may resume on `pageshow`.
      const onPageHide = (event: Event) => {
        if ((event as PageTransitionEvent).persisted) return;
        reportAbandon();
      };

      arm();
      document.addEventListener('visibilitychange', onVisibilityChange);
      global.addEventListener('pagehide', onPageHide);

      return () => {
        global.clearTimeout(timeoutHandle);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        global.removeEventListener('pagehide', onPageHide);
        if (isLoadingRef.current) reportAbandon();
      };
    }, undefined);
  }, [isLoading, stableOnTimeout, stableShouldPostpone, stableOnAbandon]);
}
